﻿<template>
    <div></div>
</template>

<style scoped>
</style>

<script lang="ts">
    //normal <script> executed in module scope (only once)
    //只會執行一次(第1次) 除非按下F5

    export default {
        name: module$.signalR,
        inheritAttrs: false
    }
</script>

<script setup lang="ts">
    import { onBeforeMount, onBeforeUnmount, ref, Ref } from "vue";
    import { utilfcty$, utility, signalrEvent$, signalrOn, signalrOff, signalrCallback, signalrInquire, signalrSend, signalrNotify, signalrGroupOp } from '../utils/utility';
    import { module$ } from '../enums'
    import { storeToRefs } from "pinia";
	import { HubConnection, HubConnectionBuilder, HubConnectionState, IHttpConnectionOptions, HttpTransportType } from '@microsoft/signalr'

    const props = defineProps<{
    }>()

    //***************工具物件***************
    const util$ = utilfcty$.vueUse();

    //***************元件屬性|計算**********
    let client: HubConnection | undefined;
    const { parameters } = storeToRefs(util$.pinia$.app);
    // 確保路徑組合時不會出現雙斜線
    const baseUrl = import.meta.env.BASE_URL.endsWith('/')
        ? import.meta.env.BASE_URL.slice(0, -1)
        : import.meta.env.BASE_URL;

    const hubPath = parameters.value['signalrUrl'].startsWith('/')
        ? parameters.value['signalrUrl']
        : `/${parameters.value['signalrUrl']}`;

    const signalrUrl = baseUrl + hubPath;
    const connectionId = ref();

    //***************函式/事件**************
    /** 建立SignalR連線 */
    const buildConnection = (url: string) => {
        if (client) {
            stop();
        }
        //每次重試間隔時間
        let threeSec = [3000, 3000, 3000, 3000, 3000, 3000];
        let reconnectInterval = [0];
        let idx = 0;
        //總重試時間540秒
        for (idx = 0; idx < 30; idx++) {
            reconnectInterval = [...reconnectInterval, ...threeSec];
        }
		// http$ 的統一 auth 設定（避免重複讀取 token 邏輯）
		const authConfig = util$.http$?.getAuthConfig?.() || {
			accessTokenFactory: () => util$.pinia$.security.token() || ''
		};
        client = new HubConnectionBuilder()
            //.withUrl(url, { accessTokenFactory: () => util$.pinia$.security.token() } as IHttpConnectionOptions)
			.withUrl(url, {
				...authConfig, // ✅ 自動帶入 Bearer token
				transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling
			} as IHttpConnectionOptions)
            .withAutomaticReconnect(reconnectInterval)
            .build();
        util$.log$.info('A SignalR connection instance has been created.')
    }

    /** 連線SignalR*/
    const start = async () => {
        if (!client) return;
        try {
            await client.start();
            connectionId.value = client.connectionId;
            // --- 關鍵：注入所有方法到 Utility ---
            // 呼叫橋接，所有參數型態必須與 ISignalRBridge 完全吻合
            utility.signalR$.___setBridge___({
                // (name: string, cb: signalrCallback) => void
                on: (name: string, cb: signalrCallback): void => {
                    client?.on(name, cb);
                },

                // (name: string, cb?: signalrCallback) => void
                off: (name: string, cb?: signalrCallback): void => {
                    if (cb) {
                        client?.off(name, cb);
                    } else {
                        client?.off(name);
                    }
                },

                // (name: string, ...args: any[]) => Promise<void>
                send: async (name: string, ...args: any[]): Promise<void> => {
                    // 送出前檢查連線狀態:未連線(斷線/重連中/啟動中)則靜默跳過,
                    // 避免 'Cannot send data if the connection is not in the Connected State'。
                    // fire-and-forget 語意:連上後由 ready/reconnected 事件補償重拉,不需在此報錯。
                    if (client?.state !== HubConnectionState.Connected) {
                        util$.log$.warn(`[SignalR] send('${name}') skipped: connection not connected (state=${client?.state}).`);
                        return;
                    }
                    await client.send(name, ...args);
                },

                // (handler: string, func: string, message: string) => Promise<string>
                inquire: async <T>(handler: string, func: string, message: any): Promise<T> => {
                    // RPC 模式:呼叫端在 await 結果,未連線不能靜默吞(否則永遠等不到),須明確 throw。
                    if (client?.state !== HubConnectionState.Connected) {
                        throw new Error(`[SignalR] inquire skipped: connection not connected (state=${client?.state}).`);
                    }
                    // SignalR 的 invoke<T> 會自動根據 T 進行 JSON 反序列化
                    const response = await client.invoke<T>('Inquire', handler, func, message);
                    return response as T;
                },

                // (handler: string, func: string, message: string) => Promise<void>
                notify: async (handler: string, func: string, message: string): Promise<void> => {
                    // 送出前檢查連線狀態:未連線則靜默跳過(snapshot 由 ready/reconnected 補償重拉)。
                    // 這是 requestSnapshot 觸發「not Connected」錯誤的修正點:進頁/按重新整理時若連線
                    // 尚未就緒或斷線中,跳過送出而非拋錯。
                    if (client?.state !== HubConnectionState.Connected) {
                        util$.log$.warn(`[SignalR] notify('${func}') skipped: connection not connected (state=${client?.state}).`);
                        return;
                    }
                    await client.send('Notify', handler, func, message);
                },

                // (group: string) => Promise<void>
                addToGroup: async (handler: string, func: string, group: string): Promise<void> => {
					await client?.invoke("AddToGroup", handler, func, group);
                },

                // (group: string) => Promise<void>
				removeFromGroup: async (handler: string, func: string, group: string): Promise<void> => {
					await client?.invoke("RemoveFromGroup", handler, func, group);
                }
            });
            utility.signalR$.emit(signalrEvent$.connected)
            // 觸發 Ready (此時 Pending Registry 會自動清空並註冊)
            utility.signalR$.emit(signalrEvent$.ready);
            util$.log$.info('Connected to SignalR successfully.', connectionId.value);
        } catch (err) {
            console.error("SignalR Start Error", err);
			// 啟動失敗也走統一錯誤處理（例如初始 401）
			util$.http$?.handleError?.(err, { silent: false });
        }
    }

    /** 中斷SignalR連線 */
    const stop = async () => {
        if (client) {
            await client.stop();
            // 清理 Bridge 參考
            utility.signalR$.___setBridge___({
                on: () => { }, off: () => { },
                send: async () => { },
                inquire: async <T>(): Promise<T> => null as any,
                notify: async () => { },
                addToGroup: async () => { },
                removeFromGroup: async () => { }
            });
            util$.log$.info('The SignalR connection has been terminated.')
            client = undefined;
        }
    }

    const initialize = async () => {

        buildConnection(signalrUrl);
        await start();

        //SignalR連線關閉
        client?.onclose((error) => {
			// 呼叫 http$ 的統一錯誤處理（401 會自動導向登入）
			if (error) {
				// 可選：額外記錄 SignalR 專屬資訊
				util$.log$.warn(`[SignalR] Connection closed: ${error.message}`);

				// 統一錯誤處理（會負責 401 導向 + toast + 通用 log）
				util$.http$?.handleError?.(error, { silent: false, skipRedirect: false });
			} else {
                util$.log$.info('Connection closed gracefully by the client.');
            }
            utility.signalR$.emit(signalrEvent$.disconnected);
        })
        //SignalR重新連線中...
        client?.onreconnecting((error) => {
            util$.log$.warn('SignalR reconnecting...', error);
            utility.signalR$.emit(signalrEvent$.reconnecting);
        })
        //重新連接後處理
        client?.onreconnected(async (newConnectionId) => {
			const prevConnectionId = connectionId.value;
            connectionId.value = newConnectionId;
            util$.log$.warn('SignalR reconnection successful.', newConnectionId);
			await client?.send("OnClientReconnected", prevConnectionId ?? "");
            utility.signalR$.emit(signalrEvent$.reconnected);
        })
        // Hub送來Exception訊息
        client?.on("onHubError", (errorObj) => {
            const errorData = JSON.parse(errorObj);
            const status = errorData?.status;

            // 統一的 log 內容（用後端送的 title，不寫死）
            const logLine = `[SignalR] ${errorData.title} | [Status ${status}] | TraceId: ${errorData.traceId} | Entry: ${errorData.entry} | Instance: ${errorData.instance}`;

            if (status === 403) {
                // 權限不足：正常的業務拒絕，非系統錯誤 → warn 級別
                util$.log$.warn(logLine);
                util$.toast$?.warn(errorData.title, errorData.detail, 'c', -1);
            } else if (status === 401) {
                // 未驗證：跳登入（或交給既有的 401 處理流程）
                util$.log$.warn(logLine);
                util$.toast$?.warn(errorData.title, errorData.detail, 'c', -1);
                // 視需要：util$.router$.replace({ name: 'login' }) 或觸發既有登入流程
            } else {
                // 500 等系統錯誤 → error 級別 + 附 TraceId 供查
                util$.log$.error(logLine);
                util$.toast$?.error(errorData.title, `${errorData.detail}\r\n\r\nTrace ID: ${errorData.traceId}`, 'c', -1);
            }
        });
    }

    //***************元件監看***************

    //***************元件鉤子***************
    onBeforeMount(async () => {
            await initialize();
    })

    onBeforeUnmount(async ()=>{
        await stop();
    })

</script>